import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Pencil, Trash2, ExternalLink, Search, ArrowUpDown, ArrowUp, ArrowDown, Clock, Filter } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const LIVES_RUBRIQUE_ID = "61b18e64-ce6f-48fa-a568-85b65fffebab";
const NEWSLETTER_RUBRIQUE_ID = "63397fe3-f685-42f7-be07-a1d44b74d7a1";

function SortIndicator({ field, sortField, sortDir }) {
  if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
  return sortDir === "asc" ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />;
}

function Countdown({ targetDate }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const target = new Date(targetDate).getTime();
  const diff = target - now;
  if (diff <= 0) return <span className="text-xs text-amber-600 font-medium">Publication imminente</span>;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  let text = "";
  if (days > 0) text = `${days}j ${hours}h`;
  else if (hours > 0) text = `${hours}h ${mins}min`;
  else text = `${mins}min`;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium" data-testid="countdown-badge">
      <Clock className="h-3 w-3" /> Dans {text}
    </span>
  );
}

export default function ArticleList() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    try {
      const res = await api.get(`/articles?admin=true&status=all&limit=200&exclude_rubriques=${LIVES_RUBRIQUE_ID},${NEWSLETTER_RUBRIQUE_ID}`);
      setArticles(res.data.articles);
    } catch { /* load error */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const deleteArticle = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try { await api.delete(`/articles/${id}`); toast.success("Article supprimé"); load(); } catch { toast.error("Erreur"); }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const statusCounts = useMemo(() => {
    const counts = { all: articles.length, published: 0, draft: 0, scheduled: 0 };
    articles.forEach(a => { if (counts[a.status] !== undefined) counts[a.status]++; });
    return counts;
  }, [articles]);

  const filtered = useMemo(() => {
    let list = [...articles];
    if (statusFilter !== "all") {
      list = list.filter(a => a.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a => a.title?.toLowerCase().includes(q) || a.slug?.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      let valA, valB;
      if (sortField === "date") {
        valA = a.publication_date || a.scheduled_date || a.created_at || "";
        valB = b.publication_date || b.scheduled_date || b.created_at || "";
      } else if (sortField === "title") {
        valA = (a.title || "").toLowerCase();
        valB = (b.title || "").toLowerCase();
      } else if (sortField === "views") {
        valA = a.views || 0;
        valB = b.views || 0;
      } else if (sortField === "status") {
        valA = a.status || "";
        valB = b.status || "";
      }
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [articles, search, sortField, sortDir, statusFilter]);

  const statusMap = { published: { label: "Publié", variant: "default" }, draft: { label: "Brouillon", variant: "secondary" }, scheduled: { label: "Programmé", variant: "outline" } };
  const reviewMap = { in_review: "En révision", approved: "Approuvé", rejected: "Rejeté", changes_requested: "Modifications" };

  const filterButtons = [
    { key: "all", label: "Tous" },
    { key: "published", label: "Publiés" },
    { key: "draft", label: "Brouillons" },
    { key: "scheduled", label: "Programmés" },
  ];

  return (
    <div data-testid="article-list">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl">Articles</h1>
        <Link to="/admin/articles/new"><Button className="rounded-none" data-testid="new-article-btn"><Plus className="h-4 w-4 mr-2" /> Nouvel article</Button></Link>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un article..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-none"
            data-testid="article-search-input"
          />
        </div>
        <div className="flex items-center gap-1" data-testid="status-filter-group">
          {filterButtons.map(fb => (
            <Button
              key={fb.key}
              variant={statusFilter === fb.key ? "default" : "ghost"}
              size="sm"
              className="rounded-none text-xs h-8"
              onClick={() => setStatusFilter(fb.key)}
              data-testid={`filter-${fb.key}`}
            >
              {fb.label}
              <span className="ml-1 opacity-60">{statusCounts[fb.key] || 0}</span>
            </Button>
          ))}
        </div>
        <span className="text-sm text-muted-foreground ml-auto">{filtered.length} article{filtered.length > 1 ? "s" : ""}</span>
      </div>

      <div className="border rounded-none">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <button onClick={() => toggleSort("title")} className="flex items-center hover:text-foreground transition-colors" data-testid="sort-title">
                  Titre <SortIndicator field="title" sortField={sortField} sortDir={sortDir} />
                </button>
              </TableHead>
              <TableHead>
                <button onClick={() => toggleSort("status")} className="flex items-center hover:text-foreground transition-colors" data-testid="sort-status">
                  Statut <SortIndicator field="status" sortField={sortField} sortDir={sortDir} />
                </button>
              </TableHead>
              <TableHead>
                <button onClick={() => toggleSort("views")} className="flex items-center hover:text-foreground transition-colors" data-testid="sort-views">
                  Vues <SortIndicator field="views" sortField={sortField} sortDir={sortDir} />
                </button>
              </TableHead>
              <TableHead>
                <button onClick={() => toggleSort("date")} className="flex items-center hover:text-foreground transition-colors" data-testid="sort-date">
                  Date <SortIndicator field="date" sortField={sortField} sortDir={sortDir} />
                </button>
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center py-8">Chargement...</TableCell></TableRow>
            : filtered.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">{search ? "Aucun résultat" : "Aucun article"}</TableCell></TableRow>
            : filtered.map((article) => (
              <TableRow key={article.id}>
                <TableCell>
                  <Link to={`/admin/articles/${article.id}/edit`} className="font-medium hover:text-primary">{article.title}</Link>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">/{article.slug}</p>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1.5">
                      <Badge variant={statusMap[article.status]?.variant || "secondary"} className="rounded-none text-xs">{statusMap[article.status]?.label || article.status}</Badge>
                      {article.review_status && reviewMap[article.review_status] && (
                        <Badge variant="outline" className="rounded-none text-[10px]">{reviewMap[article.review_status]}</Badge>
                      )}
                    </div>
                    {article.status === "scheduled" && article.scheduled_date && (
                      <Countdown targetDate={article.scheduled_date} />
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground" data-testid={`views-${article.id}`}>{article.views || 0}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {article.status === "scheduled" && article.scheduled_date
                    ? new Date(article.scheduled_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                    : article.publication_date ? new Date(article.publication_date).toLocaleDateString("fr-FR") : "\u2014"
                  }
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`article-actions-${article.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild><Link to={`/admin/articles/${article.id}/edit`}><Pencil className="h-4 w-4 mr-2" /> Modifier</Link></DropdownMenuItem>
                      {article.status === "published" && <DropdownMenuItem asChild><Link to={`/article/${article.slug}`} target="_blank"><ExternalLink className="h-4 w-4 mr-2" /> Voir</Link></DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => deleteArticle(article.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Supprimer</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
